import { useMemo, useState } from "react";
import { Close } from "@mui/icons-material";
import { Card, Grid, IconButton } from "@mui/material";

import Box from "../Box";
import Avatar from "../Avatar";
import FormField from "../FormField";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import ImageUploader from "../ImageUploader";
import useStrings from "../../hooks/useStrings";
import PhoneNumberInput from "../PhoneNumberInput";
import CustomStatusCell from "../CustomStatusCell";
import ControlLabelCheckBox from "../ControlLabelCheckBox";

import { InputAddress } from "../Address";
import { Mode, Status } from "../../constants/enums";
import { SelectMultiLanguage } from "../MultiLanguage";
import { ILableOrganization } from "../../commons/interfaces";
import { IDataDetailOrg, IOrgUserProfile } from "../../hooks/useContainerOrganization";



interface IBasicInfoStepProps {
    mode: number;
    language: string;
    hidenStatus?: boolean;
    dataDetailOrg: IDataDetailOrg;
    dataOwnerOrg?: IOrgUserProfile;
    showActionSpecifyOwner?: boolean;
    errorDetailOrg?: { [key in keyof IDataDetailOrg]?: string };

    handleChangeOwner: () => void;
    handleChangeLanguage: (val: string) => void;
    handleChangeValue: (val: any, key: string, isMulti?: boolean) => void;
}

const BasicInformationStep = ({
    mode,
    language,
    hidenStatus,
    dataOwnerOrg,
    dataDetailOrg,
    errorDetailOrg,
    lableOrganization,
    handleChangeOwner,
    handleChangeValue,
    handleChangeLanguage,
    showActionSpecifyOwner,
}: IBasicInfoStepProps & ILableOrganization) => {
    const strings = useStrings();

    const [isAddSpecifyOwner, setIsAddSpecifyOwner] = useState<boolean>(false);

    const newLableOrganization = useMemo(() => (
        (lableOrganization || strings.Common.ORGANIZATION).toLowerCase()
    ), [lableOrganization, strings]);


    return (
        <Grid container spacing={3}>
            {/* LOGO ORG */}
            <Grid item xs={12} lg={3}>
                <Card>
                    <Box p={2} textAlign="center">
                        <ImageUploader
                            width={150}
                            height={150}
                            imageRatio="1:1"
                            disabled={mode === Mode.View}
                            fileId={dataDetailOrg?.logoId}
                            onChangeImage={(data) => {
                                if (Helpers.isNullOrEmpty(data?.accessUrl)
                                    && Helpers.isNullOrEmpty(data?.id)) {
                                    return;
                                } else {
                                    const temp = {
                                        logoId: data?.id,
                                        logoUrl: data?.accessUrl
                                    };
                                    handleChangeValue(temp, "logo", false);
                                }
                            }}
                        />
                        <Typography variant="h6">
                            {strings.format(strings.Organization.IMAGE_PHOTO, newLableOrganization)}
                        </Typography>
                    </Box>
                </Card>
            </Grid>
            {/* BASIC ORG */}
            <Grid item xs={12} lg={9}>
                <Card>
                    <Box p={2}>
                        <Box alignItems="center" justifyContent="space-between" sx={{ display: "flex", flexWrap: "wrap" }}>
                            <Box alignItems="center" sx={{ display: "flex", flexWrap: "wrap" }}>
                                <Typography variant="h5">
                                    {strings.format(strings.Organization.BASIC_INFO, newLableOrganization)}
                                </Typography>
                                {
                                    !hidenStatus &&
                                    !Helpers.isNullOrEmpty(dataDetailOrg?.id) &&
                                    !Helpers.isNullOrEmpty(dataDetailOrg?.status) &&
                                    (
                                        <Box ml={2} display="flex">
                                            <CustomStatusCell isDetail status={dataDetailOrg?.status} />
                                        </Box>
                                    )}
                            </Box>
                            {
                                (mode !== Mode.View) &&
                                <SelectMultiLanguage
                                    defaultLanguage={language}
                                    onChangeLanguage={(value) => {
                                        handleChangeLanguage(value);
                                    }}
                                />
                            }
                        </Box>

                        <Box component="form">
                            <Grid container spacing={3} p={2}>
                                <Grid item xs={12} md={6}>
                                    <FormField
                                        required
                                        mode={mode}
                                        maxLength={255}
                                        errorMessage={errorDetailOrg?.name}
                                        defaultValue={dataDetailOrg?.name?.value?.[language]}
                                        onBlur={(value: any) => handleChangeValue(value, "name", true)}
                                        placeholder={strings.format(strings.Organization.ENTER_NAME, newLableOrganization)}
                                        label={Helpers.capitalizeFirstLetter(strings.format(strings.Organization.NAME, newLableOrganization))}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormField
                                        mode={mode}
                                        maxLength={255}
                                        label={strings.Organization.EMAIL}
                                        defaultValue={dataDetailOrg?.email}
                                        errorMessage={errorDetailOrg?.email}
                                        placeholder={strings.Organization.ENTER_EMAIL}
                                        onBlur={(value: any) => handleChangeValue(value, "email", false)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <PhoneNumberInput
                                        mode={mode}
                                        country={"vn"}
                                        variant="standard"
                                        defaultValue={dataDetailOrg?.phoneNumber}
                                        errorMessage={errorDetailOrg?.phoneNumber}
                                        onChangeValue={(value: any) => handleChangeValue(value, "phoneNumber", false)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormField
                                        mode={mode}
                                        maxLength={15}
                                        type={"number"}
                                        label={strings.Organization.FAX_NUMBER}
                                        defaultValue={dataDetailOrg?.faxNumber}
                                        errorMessage={errorDetailOrg?.faxNumber}
                                        placeholder={strings.Organization.ENTER_FAX_NUMBER}
                                        onBlur={(value: any) => {
                                            const newValue = value ? ((Number(value) > 0) ? Number(value) : 0).toString() : undefined;
                                            handleChangeValue(newValue, "faxNumber", false)
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormField
                                        mode={mode}
                                        maxLength={15}
                                        type={"number"}
                                        label={strings.Organization.TAX_NUMBER}
                                        defaultValue={dataDetailOrg?.taxNumber}
                                        errorMessage={errorDetailOrg?.taxNumber}
                                        placeholder={strings.Organization.TAX_NUMBER}
                                        onBlur={(value: any) => {
                                            const newValue = value ? ((Number(value) > 0) ? Number(value) : 0).toString() : undefined;
                                            handleChangeValue(newValue, "taxNumber", false);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormField
                                        mode={mode}
                                        maxLength={255}
                                        label={strings.Organization.WEBSITE}
                                        defaultValue={dataDetailOrg?.website}
                                        errorMessage={errorDetailOrg?.website}
                                        placeholder={strings.Organization.ENTER_WEBSITE}
                                        onBlur={(value: any) => {
                                            handleChangeValue(value, "website", false);
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={12}>
                                    <InputAddress
                                        isFullRow
                                        mode={mode}
                                        addressId={dataDetailOrg?.addressId}
                                        errorAddress={errorDetailOrg?.addressId}
                                        onChangeData={(value: string) => {
                                            handleChangeValue(value, "addressId", false);
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={12}>
                                    <FormField
                                        isFullRow
                                        multiline
                                        mode={mode}
                                        maxLength={255}
                                        label={strings.Organization.DESCRIPTION}
                                        placeholder={strings.Organization.ENTER_DESCRIPTION}
                                        defaultValue={dataDetailOrg?.description?.value?.[language]}
                                        onBlur={(value: any) => {
                                            handleChangeValue(value, "description", true)
                                        }}
                                    />
                                </Grid>

                                {/* OWNER */}
                                <Grid item xs={12} md={12}>
                                    {showActionSpecifyOwner &&
                                        Helpers.isNullOrEmpty(dataOwnerOrg?.id) &&
                                        <Box>
                                            <ControlLabelCheckBox
                                                isRow
                                                value={isAddSpecifyOwner}
                                                label={strings.format(strings.Organization.ADD_OWNER, newLableOrganization)}
                                                onChangeValue={(value) => {
                                                    setIsAddSpecifyOwner(value);
                                                    handleChangeValue(undefined, "owner", false)
                                                }}
                                            />
                                            {isAddSpecifyOwner &&
                                                <Box paddingLeft={"44px"}>
                                                    <FormField
                                                        mode={mode}
                                                        maxLength={255}
                                                        defaultValue={dataDetailOrg?.owner}
                                                        errorMessage={errorDetailOrg?.owner}
                                                        label={strings.Organization.EMAIL_OWNER}
                                                        placeholder={strings.format(strings.Organization.ENTER_EMAIL_OWNER, newLableOrganization)}
                                                        onBlur={(value: any) => {
                                                            handleChangeValue(value, "owner", false)
                                                        }}
                                                    />
                                                </Box>
                                            }
                                        </Box>
                                    }

                                    {!Helpers.isNullOrEmpty(dataOwnerOrg?.id) && (
                                        <Box>
                                            <Typography variant="button" fontWeight="bold">
                                                {strings.format(strings.Organization.OWNER, newLableOrganization)}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    gap: 1,
                                                    padding: "8px",
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    marginTop: "4px",
                                                    borderRadius: "8px",
                                                    marginBottom: "8px",
                                                    alignItems: "center",
                                                    border: "1px #dddddd solid",
                                                    justifyContent: "space-between",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        gap: 1,
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Avatar
                                                        alt="avt"
                                                        src={dataOwnerOrg?.avatarUrl}
                                                        text={dataOwnerOrg?.firstName || dataOwnerOrg?.email}
                                                        sx={{
                                                            width: 50,
                                                            height: 50,
                                                            marginLeft: "16px",
                                                            marginRight: "16px",
                                                            border: "1px #dddddd solid",
                                                        }}
                                                    />
                                                    <Box display="inline-grid">
                                                        <Typography variant="button" fontWeight="bold">
                                                            {`${dataOwnerOrg?.lastName || ""} ${dataOwnerOrg?.firstName || ""}`.trim() || dataOwnerOrg?.email || "No Name"}
                                                        </Typography>
                                                        {dataOwnerOrg?.phoneNumber &&
                                                            <Typography variant="button" color="secondary">
                                                                {"Sđt"}:&nbsp;{dataOwnerOrg?.phoneNumber}
                                                            </Typography>
                                                        }
                                                        {dataOwnerOrg?.email &&
                                                            <Typography variant="button" color="secondary">
                                                                {"Email"}:&nbsp;{dataOwnerOrg?.email}
                                                            </Typography>
                                                        }
                                                    </Box>
                                                </Box>
                                                <Box sx={{
                                                    gap: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                }} >
                                                    {(dataOwnerOrg?.status === Status.PendingInvitation) &&
                                                        <Typography color="warning" variant="caption" fontStyle="italic">
                                                            {strings.Common.PENDING_INVITATION}
                                                        </Typography>
                                                    }
                                                    {showActionSpecifyOwner &&
                                                        (mode !== Mode.View) &&
                                                        ((dataOwnerOrg?.status === Status.Active) ||
                                                            (dataOwnerOrg?.status === Status.PendingInvitation)) &&
                                                        <IconButton onClick={handleChangeOwner}>
                                                            <Close />
                                                        </IconButton>
                                                    }
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Card>
            </Grid>
        </Grid>
    );
};

export default BasicInformationStep;
